import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Grid,
  Header,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

import { getTransportTask, getTransportTasks, getDrivers } from "api/backendAPI";
import { validateDriverListResponse } from "utils/driverViewModel";
import { vehicleOptions, customerOptions, defaultDriver, filterTaskRows, isClosed, needsReconciliation, needsClosure } from "utils/taskMonitoring";
import {
  buildTimeline,
  deriveTaskRow,
  loadAtomicDetail,
  nextStop,
  openExceptions,
  validateTaskListResponse,
} from "utils/transportTaskViewModel";

import "./TransportTasks.scss";
import TransportDocuments from "./TransportDocuments";
import TransportRouteMap from "./TransportRouteMap";

const UNKNOWN = "—";
const DETAIL_CONCURRENCY = 4;
const PILOT_DETAIL_LIMIT = 200;

export function selectPilotSummaries(summaries) {
  return summaries.slice(0, PILOT_DETAIL_LIMIT);
}

async function mapWithConcurrency(items, mapper, concurrency) {
  const results = new Array(items.length);
  let nextIndex = 0;
  async function run() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]);
    }
  }
  const workers = new Array(Math.min(concurrency, items.length)).fill(null).map(run);
  await Promise.all(workers);
  return results;
}

function formatDate(value, language) {
  if (!value) return UNKNOWN;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return UNKNOWN;
  return new Intl.DateTimeFormat(language === "fi" ? "fi-FI" : "en-GB", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function statusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "active": return "green";
    case "blocked": return "red";
    case "planned": return "blue";
    case "completed": return "grey";
    default: return "yellow";
  }
}

function statusName(status, t) {
  return t(`transportTasks.statuses.${status || "unknown"}`);
}

function valueOrMissing(value, t) {
  return value ? value : <span className="transportTasks__missing">{t("transportTasks.missing")}</span>;
}

export function assignmentDriverName(assignment) {
  return assignment && (assignment.driver_display_name || assignment.driver_id);
}

function ownerName(profile, t) {
  if (profile === "junior") return t("transportTasks.ownerProfiles.junior");
  if (profile === "senior") return t("transportTasks.ownerProfiles.senior");
  return t("transportTasks.ownerProfiles.unassigned");
}

function detailUncertainty(row, t) {
  const key = row.detailState === "notLoaded" ? "notLoaded" : row.detailState === "contract" ? "contractUnknown" : row.detailState === "stale" ? "staleUnknown" : "uncertain";
  return <Label basic color="orange" data-testid="transport-task-uncertain">{t(`transportTasks.${key}`)}</Label>;
}

function EmptyState({ t }) {
  return (
    <Message icon data-testid="transport-task-empty">
      <Icon name="check circle outline" />
      <Message.Content>
        <Message.Header>{t("transportTasks.emptyTitle")}</Message.Header>
        {t("transportTasks.emptyBody")}
      </Message.Content>
    </Message>
  );
}

export function TransportTaskListView({ rows, loading, error, t, language, drivers = [] }) {
  if (loading) return <Loader active inline="centered" content={t("transportTasks.loading")} />;
  if (error) return <Message negative header={t("transportTasks.errorTitle")} content={t("transportTasks.errorBody")} />;
  if (!rows.length) return <EmptyState t={t} />;

  return (
    <div className="transportTasks__tableWrap">
      <Table selectable stackable className="transportTasks__table" data-testid="transport-task-list">
        <Table.Header>
          <Table.Row>
            {["orderCustomer", "vehicleDriver", "nextAction", "filterStatus", "attention"].map(key =>
              <Table.HeaderCell key={key}>{t(`transportTasks.${key}`)}</Table.HeaderCell>)}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map(row => {
            const assignment = row.assignment;
            const next = row.nextStop;
            const closed = isClosed(row.summary.status);
            const registryDriver = !closed && defaultDriver(assignment, drivers);
            return (
              <Table.Row key={row.summary.transport_task_id}>
                <Table.Cell>
                  <Link className="transportTasks__taskLink" to={{
                    pathname: `/transport-tasks/${row.summary.transport_task_id}`,
                    state: { minimumVersion: row.summary.version },
                  }}>{row.summary.order_ref || row.summary.title}</Link>
                  <div>{row.summary.customer_ref}</div>
                  <div className="transportTasks__meta">{row.summary.title}</div>
                </Table.Cell>
                <Table.Cell>
                  {row.uncertain ? UNKNOWN : <>
                    <strong>{valueOrMissing(assignment && assignment.vehicle_ref, t)}</strong>
                    <div>{valueOrMissing(assignmentDriverName(assignment) || registryDriver, t)}</div>
                    {registryDriver && <small className="transportTasks__meta">{t("transportTasks.defaultDriver")}</small>}
                    {assignment && (assignment.trailer_ref || assignment.equipment_ref) &&
                      <div className="transportTasks__meta">{t("transportTasks.trailer")}: {assignment.trailer_ref || assignment.equipment_ref}</div>}
                  </>}
                </Table.Cell>
                <Table.Cell>
                  {closed || row.uncertain ? UNKNOWN : next ? <>
                    <strong>{next.location_name}</strong>
                    <div>{t(`transportTasks.stopKinds.${next.kind}`)}</div>
                    {row.deadline && <div>{formatDate(row.deadline, language)}</div>}
                  </> : needsClosure(row) ? <span>{t("transportTasks.stopsFinished")}</span> :
                    <span data-testid="transport-task-missing-stop">{t("transportTasks.noNextStop")}</span>}
                  {!row.uncertain && row.detail.planned_end && <div className="transportTasks__meta">
                    {t("transportTasks.plannedEnd")}: {formatDate(row.detail.planned_end, language)}
                  </div>}
                </Table.Cell>
                <Table.Cell>
                  <Label size="small" color={statusColor(row.summary.status)}>{statusName(row.summary.status, t)}</Label>
                </Table.Cell>
                <Table.Cell>
                  {row.uncertain ? detailUncertainty(row, t) : closed ? UNKNOWN : <>
                    {row.exceptions.length > 0 && <div data-testid="transport-task-exception" className="transportTasks__overdue">
                      {row.exceptions.length} {t("transportTasks.openExceptions")}
                    </div>}
                    {needsClosure(row) ? <div className="transportTasks__missing">{t("transportTasks.closure")}</div> :
                      needsReconciliation(row) ? <div className="transportTasks__missing">{t("transportTasks.reconcile")}</div> :
                      row.deadlineState === "overdue" && <div className="transportTasks__overdue">{t("transportTasks.overdue")}</div>}
                  </>}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}

function TaskFacts({ task, etag, t, language }) {
  const assignment = task.assignment;
  const schedule = task.schedule;
  const reporting = schedule && schedule.reporting_instruction;
  return (
    <Grid stackable columns={3}>
      <Grid.Column>
        <Header as="h3">{t("transportTasks.assignment")}</Header>
        <List>
          <List.Item><strong>{t("transportTasks.owner")}:</strong> {ownerName(task.owner_agent_profile, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.vehicle")}:</strong> {valueOrMissing(assignment && assignment.vehicle_ref, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.driver")}:</strong> {valueOrMissing(assignmentDriverName(assignment), t)}</List.Item>
          <List.Item><strong>{t("transportTasks.trailer")}:</strong> {valueOrMissing(assignment && assignment.trailer_ref, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.equipment")}:</strong> {valueOrMissing(assignment && assignment.equipment_ref, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.source")}:</strong> {valueOrMissing(assignment && assignment.source, t)}</List.Item>
        </List>
      </Grid.Column>
      <Grid.Column>
        <Header as="h3">{t("transportTasks.schedule")}</Header>
        <List>
          <List.Item><strong>{t("transportTasks.plannedStart")}:</strong> {formatDate(task.planned_start, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.plannedEnd")}:</strong> {formatDate(task.planned_end, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.nextCheck")}:</strong> {formatDate(schedule && schedule.next_check_at, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.schedulerOwner")}:</strong> {valueOrMissing(schedule && schedule.scheduler_owner, t)}</List.Item>
          <List.Item><strong>{t("transportTasks.createdAt")}:</strong> {formatDate(task.created_at, language)}</List.Item>
          <List.Item><strong>{t("transportTasks.updatedAt")}:</strong> {formatDate(task.updated_at, language)}</List.Item>
        </List>
      </Grid.Column>
      <Grid.Column>
        <Header as="h3">{t("transportTasks.reporting")}</Header>
        {reporting ? (
          <List>
            <List.Item>{reporting.instruction}</List.Item>
            <List.Item><strong>{t("transportTasks.channel")}:</strong> {valueOrMissing(reporting.channel, t)}</List.Item>
            <List.Item><strong>{t("transportTasks.cadence")}:</strong> {valueOrMissing(reporting.cadence, t)}</List.Item>
            <List.Item><Label basic color={reporting.active ? "green" : "grey"}>{reporting.active ? t("transportTasks.active") : t("transportTasks.inactive")}</Label></List.Item>
          </List>
        ) : <Message warning size="small">{t("transportTasks.noReporting")}</Message>}
        <div className="transportTasks__version">v{task.version} · ETag {valueOrMissing(etag, t)}</div>
      </Grid.Column>
    </Grid>
  );
}

function StopWindow({ stop, t, language }) {
  if (stop.window_start && stop.window_end) return <span>{formatDate(stop.window_start, language)} – {formatDate(stop.window_end, language)}</span>;
  if (stop.window_start) return <span>{t("transportTasks.windowFrom")}: {formatDate(stop.window_start, language)}</span>;
  if (stop.window_end) return <span>{t("transportTasks.windowUntil")}: {formatDate(stop.window_end, language)}</span>;
  return null;
}

function Stops({ stops, t, language }) {
  if (!stops || !stops.length) return <Message warning>{t("transportTasks.noStops")}</Message>;
  return (
    <Card.Group itemsPerRow={3} stackable>
      {stops.slice().sort((a, b) => a.sequence - b.sequence).map(stop => (
        <Card key={stop.stop_id}>
          <Card.Content>
            <Card.Header>{stop.sequence}. {stop.location_name}</Card.Header>
            <Card.Meta>{t(`transportTasks.stopKinds.${stop.kind}`)} · {t(`transportTasks.stopStatuses.${stop.status}`)}</Card.Meta>
            <Card.Description>
              {stop.address && <div>{stop.address}</div>}
              <div><StopWindow stop={stop} t={t} language={language} /></div>
              {stop.instructions && <p>{stop.instructions}</p>}
            </Card.Description>
          </Card.Content>
        </Card>
      ))}
    </Card.Group>
  );
}

export function TransportTaskDetailView({ task, etag, loading, error, lastLoaded, onRefresh, t, language, documentsPanel }) {
  if (loading) return (
    <Message warning data-testid="transport-task-detail-unknown">
      <Loader active inline="centered" content={t("transportTasks.loadingUnknown")} />
    </Message>
  );
  if (error || !task) return (
    <Message negative data-testid="transport-task-detail-unknown">
      <Message.Header>{t("transportTasks.detailUnknown")}</Message.Header>
      <p>{t(`transportTasks.detailErrors.${error || "network"}`)}</p>
      <Button basic onClick={onRefresh} icon="refresh" content={t("transportTasks.refresh")} />
    </Message>
  );
  const timeline = buildTimeline(task);
  const exceptions = openExceptions(task);
  const closed = isClosed(task.status);
  const next = closed ? null : nextStop(task);
  const assignment = task.assignment || {};
  const instructions = closed ? [] : (task.instructions || []).filter(item => item.status === "active");
  return (
    <div data-testid="transport-task-detail">
      <Link to="/transport-tasks"><Icon name="arrow left" />{t("transportTasks.backToList")}</Link>
      <Button floated="right" basic onClick={onRefresh} icon="refresh" content={t("transportTasks.refresh")} />
      <Header as="h1">
        {task.order_ref || task.title}
        <Header.Subheader>{task.customer_name_snapshot || task.customer_ref}{task.order_ref && ` · ${task.title}`}</Header.Subheader>
      </Header>
      <div className="transportTasks__detailLabels">
        <Label color={statusColor(task.status)}>{statusName(task.status, t)}</Label>
        {!closed && ["high", "critical"].includes(task.priority) && <Label basic color="orange">{t(`transportTasks.priorities.${task.priority}`)}</Label>}
        {exceptions.length > 0 && <Label color="red">{exceptions.length} {t("transportTasks.openExceptions")}</Label>}
      </div>
      <div className="transportTasks__loadedAt">{t("transportTasks.lastLoaded")}: {formatDate(lastLoaded, language)}</div>
      <section className="transportTasks__overview" aria-label={t("transportTasks.currentOverview")} data-testid="transport-task-overview">
        <div>
          <Header as="h2">{t("transportTasks.vehicleDriver")}</Header>
          <dl>
            <div><dt>{t("transportTasks.vehicle")}</dt><dd>{valueOrMissing(assignment.vehicle_ref, t)}</dd></div>
            <div><dt>{t("transportTasks.driver")}</dt><dd>{assignment.driver_display_name || t("transportTasks.driverNotNamed")}</dd></div>
            {(assignment.trailer_ref || assignment.equipment_ref) && <div><dt>{t("transportTasks.trailer")}</dt><dd>{assignment.trailer_ref || assignment.equipment_ref}</dd></div>}
          </dl>
        </div>
        <div data-testid="transport-task-next-action">
          <Header as="h2">{t(closed ? "transportTasks.closedTask" : "transportTasks.nextAction")}</Header>
          {closed ? <p>{t("transportTasks.closedTaskNote")}</p> : next ? <>
            <strong>{next.location_name}</strong>
            <div>{t(`transportTasks.stopKinds.${next.kind}`)} · {t(`transportTasks.stopStatuses.${next.status}`)}</div>
            {next.address && <div>{next.address}</div>}
            <div><StopWindow stop={next} t={t} language={language} /></div>
            {next.instructions && <p className="transportTasks__instruction">{next.instructions}</p>}
          </> : <p>{t(task.stops && task.stops.length ? "transportTasks.closure" : "transportTasks.noNextStop")}</p>}
        </div>
      </section>
      {exceptions.length > 0 && <section className="transportTasks__attention" data-testid="transport-task-current-exceptions">
        <Header as="h2">{t("transportTasks.attention")}</Header>
        <ul>{exceptions.map(item => <li key={item.exception_id}>{item.description}</li>)}</ul>
      </section>}
      {instructions.length > 0 && <details className="transportTasks__disclosure transportTasks__instructions" data-testid="transport-task-current-instructions">
        <summary>{t("transportTasks.currentInstructions")} ({instructions.length})</summary>
        <ul>{instructions.map(item => <li key={item.instruction_id} className="transportTasks__instruction">{item.content}</li>)}</ul>
      </details>}
      {documentsPanel}
      <TransportRouteMap key={task.transport_task_id} task={task} />

      <details className="transportTasks__disclosure" data-testid="transport-task-route">
        <summary>{t("transportTasks.routeDetails")}</summary>
        <Stops stops={task.stops} t={t} language={language} />
      </details>

      <details className="transportTasks__disclosure" data-testid="transport-task-history">
        <summary>{t("transportTasks.historyDetails")}</summary>
        {task.description && <>
          <Header as="h3">{t("transportTasks.backgroundDescription")}</Header>
          <p className="transportTasks__description">{task.description}</p>
        </>}
        <Header as="h3">{t("transportTasks.timeline")}</Header>
      {!timeline.length ? <Message warning>{t("transportTasks.noTimeline")}</Message> : (
        <List divided relaxed className="transportTasks__timeline">
          {timeline.map(item => (
            <List.Item key={item.id}>
              <Label horizontal basic color={item.uncertain ? "orange" : undefined}>{t(`transportTasks.timelineKinds.${item.kind}`)}</Label>
              <List.Content>
                <List.Header>{item.title}</List.Header>
                <List.Description>
                  {formatDate(item.time, language)}
                  {item.source ? ` · ${item.source}` : ""}
                  {item.status ? ` · ${item.status}` : ""}
                  {item.version ? ` · ${t("transportTasks.version")} ${item.version}` : ""}
                  {item.evidenceType ? ` · ${item.evidenceType}` : ""}
                  {item.direction ? ` · ${item.direction}` : ""}
                  {item.severity ? ` · ${item.severity}` : ""}
                  {item.evidenceRef ? ` · ${t("transportTasks.evidenceRef")}: ${item.evidenceRef}` : ""}
                  {item.outcome ? ` · ${t("transportTasks.outcome")}: ${item.outcome}` : ""}
                  {item.reason ? ` · ${t("transportTasks.reason")}: ${item.reason}` : ""}
                  {item.recordedBy ? ` · ${t("transportTasks.recordedBy")}: ${item.recordedBy}` : ""}
                </List.Description>
              </List.Content>
            </List.Item>
          ))}
        </List>
      )}
      </details>
      <details className="transportTasks__disclosure" data-testid="transport-task-technical">
        <summary>{t("transportTasks.technicalDetails")}</summary>
        <p>{t("transportTasks.source")}: {task.source_system} · {task.source_ref}</p>
        <TaskFacts task={task} etag={etag} t={t} language={language} />
      </details>
    </div>
  );
}

export function TransportTaskListPage() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ rows: [], loading: true, error: false, lastLoaded: null });
  const [reloadKey, setReloadKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState("open");
  const [customerFilter, setCustomerFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [lifecycle, setLifecycle] = useState("all");
  const [drivers, setDrivers] = useState([]);
  const [driverError, setDriverError] = useState(false);

  useEffect(() => {
    let active = true;
    setDrivers([]);
    setDriverError(false);
    getDrivers().then(result => {
      if (!validateDriverListResponse(result)) throw new Error("invalid driver response");
      if (active) setDrivers(result);
    }).catch(() => { if (active) setDriverError(true); });
    return () => { active = false; };
  }, [reloadKey]);

  useEffect(() => {
    let active = true;
    async function load() {
      setState(current => ({ ...current, rows: [], loading: true, error: false }));
      try {
        const result = await getTransportTasks();
        if (!validateTaskListResponse(result.data, result.contractVersion)) throw Object.assign(new Error("invalid list contract"), { kind: "contract" });
        const summaries = result.data.items;
        const enrichment = selectPilotSummaries(summaries);
        const initialRows = summaries.map((summary, index) => deriveTaskRow(summary, null, null, new Date(), index < PILOT_DETAIL_LIMIT ? "loading" : "notLoaded"));
        // Keep a loading indicator until detail enrichment finishes; no temporary false alarms.
        const enrichedRows = await mapWithConcurrency(enrichment, async summary => {
          try {
            const response = await loadAtomicDetail(() => getTransportTask(summary.transport_task_id), summary.transport_task_id, summary.version);
            return deriveTaskRow(summary, response.data, response.etag, new Date(), null, response.contractVersion);
          } catch (error) {
            return deriveTaskRow(summary, null, null, new Date(), error.kind || "network");
          }
        }, DETAIL_CONCURRENCY);
        const rows = enrichedRows.concat(initialRows.slice(PILOT_DETAIL_LIMIT));
        if (active) setState({ rows, loading: false, error: false, lastLoaded: new Date().toISOString() });
      } catch (error) {
        if (active) setState(current => ({ ...current, rows: [], loading: false, error: error.kind || "network" }));
      }
    }
    load();
    return () => { active = false; };
  }, [reloadKey]);

  const customers = useMemo(() => customerOptions(state.rows), [state.rows]);
  const vehicles = useMemo(() => vehicleOptions(state.rows, drivers), [state.rows, drivers]);
  const filteredRows = useMemo(() => filterTaskRows(state.rows, statusFilter, customerFilter, search, attentionOnly, lifecycle, vehicleFilter), [state.rows, statusFilter, customerFilter, search, attentionOnly, lifecycle, vehicleFilter]);

  return (
    <main className="pageShell transportTasks">
      <Helmet title={t("transportTasks.title")} />
      <header className="pageHeader">
        <div>
          <Header as="h1">{t("transportTasks.title")}</Header>
          <p className="pageHeader__lead">{t("transportTasks.lead")}</p>
        </div>
        <Button className="refreshButton" onClick={() => setReloadKey(key => key + 1)} icon="refresh" content={t("transportTasks.refresh")} />
      </header>
      {state.lastLoaded && (
        <div className="transportTasks__sourceBar">
          <span>{t("transportTasks.lastLoaded")}: {formatDate(state.lastLoaded, i18n.language)}</span>
        </div>
      )}
      <Segment className="transportTasks__filters">
        <label>{t("transportTasks.show")} <select value={statusFilter} onChange={event => { setStatusFilter(event.target.value); setLifecycle("all"); }}>
          {["open", "closed", "draft", "all"].map(value => <option key={value} value={value}>{t(`transportTasks.filters.${value}`)}</option>)}
        </select></label>{" "}
        <label>{t("transportTasks.filterCustomer")} <select value={customerFilter} onChange={event => setCustomerFilter(event.target.value)}>
          <option value="">{t("transportTasks.allCustomers")}</option>
          {customers.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select></label>
        <label>{t("transportTasks.vehicleDriver")} <select value={vehicleFilter} onChange={event => setVehicleFilter(event.target.value)}>
          <option value="">{t("transportTasks.allVehicles")}</option>
          {vehicles.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select></label>
        <label>{t("transportTasks.search")} <input type="search" value={search} placeholder={t("transportTasks.searchPlaceholder")} onChange={event => setSearch(event.target.value)} /></label>
        <label className="transportTasks__attentionFilter"><input type="checkbox" checked={attentionOnly} onChange={event => setAttentionOnly(event.target.checked)} />{t("transportTasks.attentionOnly")}</label>
        <details><summary>{t("transportTasks.moreFilters")}</summary>
          <label>{t("transportTasks.filterStatus")} <select value={lifecycle} onChange={event => setLifecycle(event.target.value)}>
            {["all", ...(statusFilter === "open" ? ["planned", "active", "blocked"] : statusFilter === "closed" ? ["completed", "cancelled"] : statusFilter === "draft" ? ["draft"] : ["draft", "planned", "active", "blocked", "completed", "cancelled"])].map(value => <option key={value} value={value}>{t(`transportTasks.filters.${value}`)}</option>)}
          </select></label>
        </details>
        <span className="transportTasks__filterCount" aria-live="polite">{!state.loading && !state.error && <>{t("transportTasks.visible")}: {filteredRows.length}</>}</span>
      </Segment>
      {state.rows.length >= 200 && <Message warning>{t("transportTasks.listLimit")}</Message>}
      {driverError && <Message warning>{t("transportTasks.driverUnavailable")}</Message>}
      <TransportTaskListView {...state} rows={filteredRows} drivers={drivers} t={t} language={i18n.language} />
    </main>
  );
}

export function TransportTaskDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const minimumVersion = location.state && Number.isInteger(location.state.minimumVersion) ? location.state.minimumVersion : null;
  const [state, setState] = useState({ task: null, etag: null, loading: true, error: null, lastLoaded: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setState(current => ({ ...current, task: null, etag: null, loading: true, error: null }));
    loadAtomicDetail(() => getTransportTask(id), id, minimumVersion)
      .then(response => active && setState({ task: response.data, etag: response.etag, loading: false, error: null, lastLoaded: new Date().toISOString() }))
      .catch(error => active && setState(current => ({ ...current, task: null, etag: null, loading: false, error: error.kind || "network" })));
    return () => { active = false; };
  }, [id, minimumVersion, reloadKey]);

  const props = useMemo(() => ({ ...state, t, language: i18n.language, onRefresh: () => setReloadKey(key => key + 1) }), [state, t, i18n.language]);
  return (
    <main className="pageShell transportTasks">
      <Helmet title={state.task ? state.task.title : t("transportTasks.title")} />
      <TransportTaskDetailView key={id} {...props} documentsPanel={
        !state.loading && !state.error && state.task && state.task.transport_task_id.toLowerCase() === id.toLowerCase() &&
          <TransportDocuments key={id} taskId={id} reloadKey={reloadKey} t={t} language={i18n.language} />
      } />
    </main>
  );
}

export default TransportTaskListPage;
